import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Mail } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { GMC_NUMBER_PATTERN, type AddMemberInput } from "@handoverpro/shared";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";

// A single "email or GMC" input — the E2.1 add-existing flow. The user types one
// identifier; we split it into the `{ email }` / `{ gmcNumber }` shape the API
// wants (mirrors `addMemberSchema` in @handoverpro/shared, which rejects "both"
// or "neither"). A 7-digit string is a GMC number; anything else is treated as an
// email and validated as one.
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const addMemberFormSchema = z.object({
  identifier: z
    .string()
    .trim()
    .min(1, "Enter an email address or GMC number")
    .refine((v) => GMC_NUMBER_PATTERN.test(v) || emailPattern.test(v), {
      message: "Enter a valid email address or a 7-digit GMC number",
    }),
});

type AddMemberFormValues = z.infer<typeof addMemberFormSchema>;

/** Split the single field into the API's one-of-{email,gmcNumber} payload. */
function toAddMemberInput(identifier: string): AddMemberInput {
  const value = identifier.trim();
  return GMC_NUMBER_PATTERN.test(value) ? { gmcNumber: value } : { email: value };
}

export interface AddMemberDialogProps {
  open: boolean;
  onSubmit: (input: AddMemberInput) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  /** A server-side error (e.g. a 404 "no such user") surfaced above the actions. */
  serverError?: string | null;
  /**
   * Switch to the email-invite flow (E3.3) — the fallback when no registered
   * user matches. Wired by the roster to close this dialog and open the invite
   * one.
   */
  onInviteInstead?: () => void;
}

/**
 * Add-member modal (HAN-87). The primary path is the existing-user search (E2.1):
 * look a registered user up by email or GMC and add them. When no user matches,
 * the admin falls back to an email invite — the "Invite by email instead"
 * affordance below points at that flow (the invite UI lands in E3.3).
 */
export function AddMemberDialog({
  open,
  onSubmit,
  onOpenChange,
  serverError = null,
  onInviteInstead,
}: AddMemberDialogProps): JSX.Element {
  const form = useForm<AddMemberFormValues>({
    resolver: zodResolver(addMemberFormSchema),
    defaultValues: { identifier: "" },
    mode: "onBlur",
  });

  // Clear the field whenever the dialog (re)opens, so a previous attempt's value
  // never lingers.
  useEffect(() => {
    if (open) form.reset({ identifier: "" });
    // form.reset is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isSubmitting = form.formState.isSubmitting;

  const handleSubmit = form.handleSubmit((values) => onSubmit(toAddMemberInput(values.identifier)));

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a member</DialogTitle>
          <DialogDescription>
            Find a registered colleague by their email address or GMC number to add them to this
            ward.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form noValidate onSubmit={handleSubmit} className="space-y-5">
            <FormField
              control={form.control}
              name="identifier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email or GMC number</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder="alice@example.nhs.uk or 7654321"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>A 7-digit number is read as a GMC number.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter className="flex-col gap-2 sm:flex-col sm:gap-2">
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                  Add member
                </Button>
              </div>

              {/* The fallback when no registered user matches: hand off to the
                  email-invite flow (E3.3). */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isSubmitting || !onInviteInstead}
                className="self-start text-muted-foreground"
                onClick={onInviteInstead}
              >
                <Mail className="h-4 w-4" aria-hidden />
                Invite by email instead
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
